import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, Trash2, FileText, Mail, Send, X, Check } from 'lucide-react';
import {
  getBozza, scartaBozza, emettiDaBozza,
  aggiungiVoceExtra, modificaVoceExtra, eliminaVoceExtra,
  aggiungiConferimentiBozza, rimuoviConferimentoBozza, aggiornaIvaConferimentoBozza,
  aggiornaTrasportoConferimentoBozza,
  aggiornaDicituraConferimentoBozza,
  aggiornaMetodoPagamentoBozza, getDaFatturare, METODI_PAGAMENTO,
  aggiornaRigaUscitaBozza, aggiornaRegimeUscitaBozza,
  rimuoviUscitaBozza, aggiornaScadenzaBozza, aggiornaRegimeBozza,
  REGIMI_IVA,
  type BozzaResponse, type FatturaResponse, type DaFatturareItem, type VoceExtraInput,
  type MetodoPagamento, type DicituraModo, type IdoneitaProblema, type RegimeIva,
} from '../../api/fatture';
import { getCliente } from '../../api/clienti';
import { getImpiantoSettings } from '../../api/impianto_settings';
import { fmtKg } from '../../lib/saldo';
import { openPdfInTab } from '../../utils/pdfPreview';
import { apiErrorMessage } from '../../lib/apiError';
import { useCategorie } from '../../hooks/useCategorie';
import { categoriaLabel } from '../../lib/categorie';
import ProformaInviaModal from './ProformaInviaModal';
import IdoneitaProblemiList from './IdoneitaProblemiList';

interface Props {
  bozzaId: string;
  onClose: () => void;
  onEmessa: (f: FatturaResponse) => void;
  onScartata: () => void;
}

const inputCls = 'text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white';
const numCls = inputCls + ' w-24 text-right';

// Data ISO di oggi + n giorni, per il prefill della scadenza. `toISOString`
// lavorerebbe in UTC e in Italia (UTC+1/+2) restituirebbe IERI per tutta la
// notte: la data si compone dai campi locali.
function isoFraGiorni(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function eur(x: string | number): string {
  return Number(x).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
}

function apiDetail(err: unknown, fallback: string): string {
  // I 422 dell'emissione bozza ritornano detail={message, ...}: estrai message,
  // altrimenti delega ad apiErrorMessage (gestisce stringa + array-422, anti React #31).
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && 'message' in detail) {
    const m = (detail as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return apiErrorMessage(err, fallback);
}

type ExtraDraft = { descrizione: string; quantita: string; prezzo_unitario_eur: string; aliquota_iva: string };
// V76 — due livelli, due draft: il REGIME e' dell'uscita, peso e prezzo sono
// della RIGA (tipologia). Fonderli rimetterebbe il prezzo sull'uscita, che e'
// esattamente cio' che questo lotto separa.
type RigaDraft = { peso_fatturazione_kg: string; prezzo_unitario: string };
const EMPTY_DRAFT: ExtraDraft = { descrizione: '', quantita: '1', prezzo_unitario_eur: '', aliquota_iva: '10' };

// Controllo override dicitura a 3 stati (predefinita/nessuna/manuale) per una riga.
// predefinita/nessuna → salva subito; manuale → mostra il testo e salva su click.
// onDirtyChange segnala al padre il "manuale digitato-ma-non-salvato" (per il guard/batch).
function DicituraControl({ modo, manuale, idBase, disabled, onDirtyChange }: {
  modo: DicituraModo; manuale: string | null; idBase: string; disabled?: boolean;
  // R3-B3: ogni interazione (modo o testo) registra SOLO come pendente (dicituraPending);
  // il salvataggio effettivo avviene col "Salva tutto". Nessun salvataggio API immediato.
  onDirtyChange?: (dirty: boolean, modo: DicituraModo, testo: string | null) => void;
}) {
  // Stato locale inizializzato dai prop; il remount (key dal chiamante su modo+manuale)
  // ri-sincronizza dopo un salvataggio — evita setState-in-effect.
  const { t } = useTranslation();
  const [m, setM] = useState<DicituraModo>(modo);
  const [txt, setTxt] = useState(manuale ?? '');
  const testoDiverso = (tt: string) => tt.trim() !== (manuale ?? '').trim();
  // Dirty = modo non-manuale cambiato rispetto al salvato, oppure testo manuale modificato.
  const dirty = (m !== 'manuale' && m !== modo) || (m === 'manuale' && testoDiverso(txt));
  return (
    <div className={'flex items-center gap-1' + (dirty ? ' ring-1 ring-accent-warning rounded' : '')} data-testid={`dic-row-${idBase}`}>
      <select className={inputCls + ' text-[11px] py-1'} data-testid={`dic-modo-${idBase}`} value={m} disabled={disabled}
        onChange={(e) => {
          const nm = e.target.value as DicituraModo;
          setM(nm);
          // predefinita/nessuna: pendente se cambia rispetto al salvato (niente auto-save).
          if (nm !== 'manuale') onDirtyChange?.(nm !== modo, nm, null);
          else onDirtyChange?.(testoDiverso(txt), 'manuale', txt);
        }}>
        <option value="predefinita">{t('fatturazione.bozza.dicitura.predefinita')}</option>
        <option value="nessuna">{t('fatturazione.bozza.dicitura.nessuna')}</option>
        <option value="manuale">{t('fatturazione.bozza.dicitura.manuale')}</option>
      </select>
      {m === 'manuale' && (
        <>
          <input className={inputCls + ' text-[11px] py-1 w-40'} data-testid={`dic-testo-${idBase}`} value={txt}
            placeholder={t('fatturazione.bozza.dicitura.placeholder')}
            onChange={(e) => { setTxt(e.target.value); onDirtyChange?.(testoDiverso(e.target.value), 'manuale', e.target.value); }} />
          {/* Il ✓ è una CONFERMA LOCALE (R3-B2): registra il testo manuale come pendente,
              NON salva subito. Guardia sul testo vuoto. */}
          <button type="button" className={(dirty ? 'text-white bg-sage rounded px-1 ' : 'text-sage-dark ') + 'hover:text-sage disabled:opacity-40'}
            disabled={disabled || !txt.trim()} title={t('fatturazione.bozza.conferma')}
            data-testid={`dic-salva-${idBase}`} onClick={() => onDirtyChange?.(testoDiverso(txt), 'manuale', txt)} aria-label={t('fatturazione.bozza.conferma')}>
            <Check size={14} />
          </button>
        </>
      )}
    </div>
  );
}

export default function BozzaWorkspace({ bozzaId, onClose, onEmessa, onScartata }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { categorie } = useCategorie();   // R4-A: lookup codice → nome_visualizzato
  const [bozza, setBozza] = useState<BozzaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Voce extra: nuova riga + map di edit per le righe esistenti.
  const [draft, setDraft] = useState<ExtraDraft>(EMPTY_DRAFT);
  const [edits, setEdits] = useState<Record<string, ExtraDraft>>({});
  // Override IVA per-conferimento: stesso pattern degli edit voci extra (map per id,
  // riseedata dalla response; gli edit non salvati si perdono al refresh).
  const [ivaEdits, setIvaEdits] = useState<Record<string, { iva_riga: string; iva_trasporto: string }>>({});
  // Importo del TRASPORTO per conferimento: stesso pattern a map, valore a
  // video (cioe' l'effettivo: override se c'e', tariffa dell'ordine altrimenti).
  // «Torna alla tariffa» non passa di qui — manda `null` subito, perche' uno
  // stato «svuotato» in piu' nella stringa si confonderebbe col campo vuoto.
  const [trasportoEdits, setTrasportoEdits] = useState<Record<string, string>>({});
  // V72 — regime per uscita; V76 — peso/prezzo per riga. Stesso pattern a map
  // degli altri edit, ma due map perche' le chiavi sono due cose diverse.
  const [regimeEdits, setRegimeEdits] = useState<Record<string, RegimeIva>>({});
  const [rigaEdits, setRigaEdits] = useState<Record<string, RigaDraft>>({});
  // Scadenza come BOZZA LOCALE: entra nel batch come le altre modifiche,
  // invece di salvare a ogni tasto premuto sul datepicker.
  // `null` = non toccata (si mostra il prefill derivato); '' = svuotata di
  // proposito, che per il backend significa «torna al default d'impianto».
  const [scadenza, setScadenza] = useState<string | null>(null);
  // Prefill: oggi + giorni d'impianto. Serve solo quando la bozza non ha
  // ancora una scadenza esplicita; a 0 il campo nasce su oggi, che e' giusto.
  const [giorniScadenza, setGiorniScadenza] = useState(30);
  // Cliente privato (nessuna P.IVA): il regime NON e' modificabile e il
  // backend risponde 422 all'override. Il select si disabilita per non far
  // scoprire la regola con un errore.
  const [clientePrivato, setClientePrivato] = useState(false);
  // ID delle RIGHE che il 422 dell'emissione ha indicato come incomplete: con
  // due tipologie sulla stessa uscita il codice non direbbe quale delle due.
  const [righeIncomplete, setRigheIncomplete] = useState<string[]>([]);
  // Pending dicitura manuale (digitato-non-salvato) segnalato da DicituraControl.
  // Chiave = idBase del controllo (mat-/tra-/extra-{id}); valore = thunk di salvataggio.
  const [dicituraPending, setDicituraPending] = useState<Record<string, () => Promise<BozzaResponse>>>({});

  // Aggiunta conferimenti (selettore inline).
  const [showAddConf, setShowAddConf] = useState(false);
  const [fatturabili, setFatturabili] = useState<DaFatturareItem[]>([]);
  const [selConf, setSelConf] = useState<Set<string>>(new Set());

  // Modali / conferme.
  const [showInvia, setShowInvia] = useState(false);
  const [confirm, setConfirm] = useState<'emetti' | 'scarta' | null>(null);
  // Guard "modifiche non salvate" prima di Genera pro forma / Emetti.
  const [pendingGuard, setPendingGuard] = useState<'genera' | 'emetti' | null>(null);
  // C7: campi mancanti FatturaPA restituiti dal 422 dell'emissione (elenco + link).
  const [emitProblemi, setEmitProblemi] = useState<IdoneitaProblema[] | null>(null);
  const [emitTitolo, setEmitTitolo] = useState<string>('');

  const applyBozza = useCallback((b: BozzaResponse) => {
    setBozza(b);
    setEdits(Object.fromEntries(b.voci_extra.map((v) => [v.id, {
      descrizione: v.descrizione, quantita: String(v.quantita),
      prezzo_unitario_eur: String(v.prezzo_unitario_eur), aliquota_iva: String(v.aliquota_iva),
    }])));
    setIvaEdits(Object.fromEntries(b.conferimenti.map((c) => [c.conferimento_id, {
      iva_riga: String(c.iva_materiale), iva_trasporto: String(c.iva_trasporto),
    }])));
    setTrasportoEdits(Object.fromEntries(b.conferimenti.map(
      (c) => [c.conferimento_id, String(c.imponibile_trasporto)])));
    setRegimeEdits(Object.fromEntries(b.uscite.map((u) => [u.uscita_id, u.regime_iva])));
    setRigaEdits(Object.fromEntries(b.uscite.flatMap((u) => u.righe.map((r) => [r.riga_id, {
      peso_fatturazione_kg: String(r.peso_fatturazione_kg),
      // `prezzo_unitario` nasce NULL e il campo deve restare vuoto, non '0':
      // uno zero scritto sembra un prezzo deciso, e l'emissione lo rifiuta
      // comunque (`gt=0`) dicendo «prezzo mancante» su un campo compilato.
      prezzo_unitario: r.prezzo_unitario === null ? '' : String(r.prezzo_unitario),
    }]))));
    setScadenza(null);
    setDicituraPending({});   // dopo ogni salvataggio/refresh i controlli dicitura sono puliti
  }, []);

  // Registra/azzera il pending dicitura manuale per un controllo (key = idBase).
  const setDicPending = useCallback((key: string, dirty: boolean, save: () => Promise<BozzaResponse>) => {
    setDicituraPending((p) => {
      if (!dirty) {
        if (!(key in p)) return p;
        const next = { ...p }; delete next[key]; return next;
      }
      return { ...p, [key]: save };
    });
  }, []);

  const load = useCallback(() => {
    void (async () => {
      setLoading(true); setError(null);
      try { applyBozza(await getBozza(bozzaId)); }
      catch (e) { setError(apiDetail(e, t('fatturazione.bozza.errore_carica'))); }
      finally { setLoading(false); }
    })();
  }, [bozzaId, applyBozza, t]);
  useEffect(() => { load(); }, [load]);

  // Contorno della bozza: giorni di scadenza d'impianto (prefill) e P.IVA del
  // cliente (regime bloccato se privato). Falliscono in silenzio: sono
  // migliorie della resa, non dati della fattura — un workspace che non si
  // apre perche' una delle due ha dato errore sarebbe molto peggio.
  const customerId = bozza?.customer_id;
  useEffect(() => {
    void (async () => {
      try { setGiorniScadenza((await getImpiantoSettings()).giorni_scadenza_default ?? 30); }
      catch (e) { console.error('impianto-settings', e); }
    })();
  }, []);
  useEffect(() => {
    if (!customerId) return;
    void (async () => {
      try {
        const c = await getCliente(customerId);
        setClientePrivato(!(c.piva ?? '').trim());
      } catch (e) { console.error('cliente', e); }
    })();
  }, [customerId]);

  async function run<T>(fn: () => Promise<T>, onOk?: (r: T) => void) {
    setBusy(true); setError(null);
    try { const r = await fn(); onOk?.(r); }
    catch (e) { setError(apiDetail(e, t('fatturazione.bozza.errore_generico'))); }
    finally { setBusy(false); }
  }

  // ── Voci extra ──
  function addVoce() {
    const voce: VoceExtraInput = {
      descrizione: draft.descrizione.trim(),
      quantita: Number(draft.quantita), prezzo_unitario_eur: Number(draft.prezzo_unitario_eur),
      aliquota_iva: Number(draft.aliquota_iva),
    };
    if (!voce.descrizione) { setError(t('fatturazione.bozza.val_descrizione')); return; }
    if (!reverseCharge && !(voce.aliquota_iva > 0)) { setError(t('fatturazione.bozza.val_aliquota')); return; }
    void run(() => aggiungiVoceExtra(bozzaId, voce), (b) => { applyBozza(b); setDraft(EMPTY_DRAFT); });
  }
  function delVoce(rigaId: string) {
    void run(() => eliminaVoceExtra(bozzaId, rigaId), applyBozza);
  }

  // ── Conferimenti ──
  function openAddConf() {
    setShowAddConf(true); setSelConf(new Set());
    void (async () => {
      if (!bozza) return;
      const items = await getDaFatturare({ customer_id: bozza.customer_id });
      setFatturabili(items);
    })();
  }
  function confirmAddConf() {
    void run(() => aggiungiConferimentiBozza(bozzaId, [...selConf]), (b) => { applyBozza(b); setShowAddConf(false); });
  }
  function removeConf(cid: string) {
    void run(() => rimuoviConferimentoBozza(bozzaId, cid), applyBozza);
  }
  function removeUscita(uid: string) {
    void run(() => rimuoviUscitaBozza(bozzaId, uid), applyBozza);
  }
  function patchRegime(uid: string, regime: RegimeIva) {
    setRegimeEdits((m) => {
      // Il REGIME e' di TESTATA: una fattura di vendita ne ha uno solo. Con 2+
      // uscite si propaga a tutte (e si salva col PATCH a livello bozza),
      // altrimenti il backend rifiuterebbe la prima toccata perche' divergente
      // dalle altre.
      if (Object.keys(m).length > 1) {
        return Object.fromEntries(Object.keys(m).map((k) => [k, regime]));
      }
      return { ...m, [uid]: regime };
    });
  }
  function patchRiga(rid: string, patch: Partial<RigaDraft>) {
    setRigheIncomplete([]);   // l'utente sta compilando: l'evidenziazione del 422 scade
    setRigaEdits((m) => ({ ...m, [rid]: { ...m[rid]!, ...patch } }));
  }
  function patchIva(cid: string, patch: Partial<{ iva_riga: string; iva_trasporto: string }>) {
    setIvaEdits((m) => ({ ...m, [cid]: { ...m[cid]!, ...patch } }));
  }
  function patchTrasporto(cid: string, valore: string) {
    setTrasportoEdits((m) => ({ ...m, [cid]: valore }));
  }
  // `null` = torna alla tariffa dell'ordine. Salva SUBITO: e' un ritorno al
  // default, non un valore in corso di digitazione.
  function resetTrasporto(cid: string) {
    void run(() => aggiornaTrasportoConferimentoBozza(bozzaId, cid, null), applyBozza);
  }
  function saveMetodoPagamento(value: string) {
    const metodo = (value || null) as MetodoPagamento | null;
    void run(() => aggiornaMetodoPagamentoBozza(bozzaId, metodo), applyBozza);
  }

  // ── Dirty derivato (nessun nuovo stato persistito) ──
  const voceDirty = (v: BozzaResponse['voci_extra'][number]): boolean => {
    const e = edits[v.id];
    return !!e && (e.descrizione !== v.descrizione || e.quantita !== String(v.quantita)
      || e.prezzo_unitario_eur !== String(v.prezzo_unitario_eur) || e.aliquota_iva !== String(v.aliquota_iva));
  };
  const ivaDirty = (c: BozzaResponse['conferimenti'][number]): boolean => {
    const e = ivaEdits[c.conferimento_id];
    return !!e && (e.iva_riga !== String(c.iva_materiale) || e.iva_trasporto !== String(c.iva_trasporto));
  };
  // Campo svuotato = non e' una modifica: azzerare si fa scrivendo 0, e un
  // input vuoto mandato come Number() diventerebbe 0 senza che nessuno l'abbia
  // deciso.
  //
  // Confronto NUMERICO, non fra stringhe: con tariffa '200.00' riscrivere
  // "200" risultava modificato, "Salva tutto" congelava un override identico
  // alla tariffa, e da li' in poi un cambio di tariffa sull'ordine non
  // arrivava piu' in bozza — con «Torna alla tariffa» comparso senza che
  // nessuno l'avesse scelto.
  const trasportoDirty = (c: BozzaResponse['conferimenti'][number]): boolean => {
    const e = trasportoEdits[c.conferimento_id];
    if (e === undefined || e.trim() === '') return false;
    return Number(e) !== Number(c.imponibile_trasporto);   // NaN !== x: un valore illeggibile resta modificato
  };
  const regimeDirty = (u: BozzaResponse['uscite'][number]): boolean =>
    regimeEdits[u.uscita_id] !== undefined && regimeEdits[u.uscita_id] !== u.regime_iva;
  const rigaDirty = (r: BozzaResponse['uscite'][number]['righe'][number]): boolean => {
    const e = rigaEdits[r.riga_id];
    return !!e && (e.peso_fatturazione_kg !== String(r.peso_fatturazione_kg)
      || e.prezzo_unitario !== (r.prezzo_unitario === null ? '' : String(r.prezzo_unitario)));
  };
  // Canale della bozza. `reverseCharge` NON si deduce dalle righe: su una
  // bozza ancora senza uscite le righe non direbbero nulla, mentre il campo
  // di testata e' la fonte che il backend usa per allineare le voci extra.
  const vendita = bozza?.tipo === 'vendita';
  const reverseCharge = bozza?.regime_iva === 'reverse_charge';
  const dirtyVoci = bozza ? bozza.voci_extra.filter(voceDirty) : [];
  const dirtyIvaConf = bozza ? bozza.conferimenti.filter(ivaDirty) : [];
  const dirtyTrasporto = bozza ? bozza.conferimenti.filter(trasportoDirty) : [];
  const dirtyRegimi = bozza ? bozza.uscite.filter(regimeDirty) : [];
  const dirtyRighe = bozza
    ? bozza.uscite.flatMap((u) => u.righe.filter(rigaDirty).map((r) => [u, r] as const)) : [];
  // Cambio di regime a livello BOZZA: vale solo da 2 uscite in su, dove il
  // PATCH per uscita non puo' funzionare. Con una sola resta il per-uscita.
  const regimeBozza = bozza && bozza.uscite.length > 1
    ? regimeEdits[bozza.uscite[0].uscita_id] ?? null : null;
  const regimeBozzaDirty = !!regimeBozza && regimeBozza !== bozza?.regime_iva;
  // Valore a video: la scadenza salvata, oppure — finche' non si tocca il
  // campo — il prefill oggi + giorni d'impianto.
  const scadenzaValore = scadenza ?? bozza?.data_scadenza ?? isoFraGiorni(giorniScadenza);
  // Pendente SOLO dopo che l'operatore ha toccato il campo. Contare il prefill
  // come modifica non salvata farebbe scattare il guard pre-emissione su ogni
  // bozza appena aperta, e un guard che scatta sempre si impara a ignorare.
  const scadenzaDirty = scadenza !== null && scadenza !== (bozza?.data_scadenza ?? '');
  const dicituraPendingKeys = Object.keys(dicituraPending);
  const pendingCount = dirtyVoci.length + dirtyIvaConf.length + dicituraPendingKeys.length
    + dirtyRighe.length + (regimeBozza ? (regimeBozzaDirty ? 1 : 0) : dirtyRegimi.length)
    + dirtyTrasporto.length + (scadenzaDirty ? 1 : 0);

  // Batch-save: await in sequenza sulle API sottostanti (no run → propaga l'errore).
  async function salvaPendenti(): Promise<void> {
    let last: BozzaResponse | null = null;
    for (const v of dirtyVoci) {
      const e = edits[v.id];
      last = await modificaVoceExtra(bozzaId, v.id, {
        descrizione: e.descrizione.trim(), quantita: Number(e.quantita),
        prezzo_unitario_eur: Number(e.prezzo_unitario_eur), aliquota_iva: Number(e.aliquota_iva),
      });
    }
    for (const c of dirtyIvaConf) {
      const e = ivaEdits[c.conferimento_id];
      last = await aggiornaIvaConferimentoBozza(bozzaId, c.conferimento_id, { iva_riga: Number(e.iva_riga), iva_trasporto: Number(e.iva_trasporto) });
    }
    for (const c of dirtyTrasporto) {
      last = await aggiornaTrasportoConferimentoBozza(
        bozzaId, c.conferimento_id, Number(trasportoEdits[c.conferimento_id]));
    }
    // Il regime PRIMA delle righe: e' un UPDATE su tutte le uscite e le voci
    // extra, e dopo di lui ai per-riga restano peso e prezzo.
    if (regimeBozza) {
      if (regimeBozzaDirty) last = await aggiornaRegimeBozza(bozzaId, regimeBozza);
    } else {
      for (const u of dirtyRegimi) {
        last = await aggiornaRegimeUscitaBozza(bozzaId, u.uscita_id, regimeEdits[u.uscita_id]);
      }
    }
    for (const [u, r] of dirtyRighe) {
      const e = rigaEdits[r.riga_id];
      // Solo i campi CAMBIATI: il BE li tratta come opzionali e rimandare un
      // prezzo vuoto come 0 lo farebbe cadere sul `gt=0` invece di lasciarlo
      // NULL, che e' lo stato «da compilare».
      const patch: Parameters<typeof aggiornaRigaUscitaBozza>[3] = {};
      if (e.peso_fatturazione_kg !== String(r.peso_fatturazione_kg)) patch.peso_fatturazione_kg = Number(e.peso_fatturazione_kg);
      if (e.prezzo_unitario !== (r.prezzo_unitario === null ? '' : String(r.prezzo_unitario))
          && e.prezzo_unitario.trim() !== '') patch.prezzo_unitario = Number(e.prezzo_unitario);
      if (Object.keys(patch).length > 0) {
        last = await aggiornaRigaUscitaBozza(bozzaId, u.uscita_id, r.riga_id, patch);
      }
    }
    if (scadenzaDirty) last = await aggiornaScadenzaBozza(bozzaId, scadenza || null);
    for (const key of dicituraPendingKeys) last = await dicituraPending[key]();
    if (last) applyBozza(last);
  }

  // "Salva tutto": persiste in un solo gesto tutte le pendenze (IVA conferimento +
  // dicitura + voci extra) via salvaPendenti(). Nessuna modifica multi-riga si perde.
  function salvaTutto() {
    void (async () => {
      setBusy(true); setError(null);
      try { await salvaPendenti(); }
      catch (e) { setError(apiDetail(e, t('fatturazione.bozza.errore_generico'))); }
      finally { setBusy(false); }
    })();
  }

  // ── Azioni ──
  function doGeneraProforma() {
    void (async () => {
      try { await openPdfInTab(`/api/fatture/bozza/${bozzaId}/proforma-pdf`); }
      catch { setError(t('fatturazione.bozza.errore_proforma')); }
    })();
  }
  function generaProforma() {
    if (pendingCount > 0) { setPendingGuard('genera'); return; }
    doGeneraProforma();
  }
  function emetti() {
    void (async () => {
      setBusy(true); setError(null); setEmitProblemi(null); setRigheIncomplete([]);
      try {
        onEmessa(await emettiDaBozza(bozzaId));
      } catch (e) {
        const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
        // C7: 422 con lista campi mancanti → elenco strutturato; altrimenti messaggio generico.
        // V76: {message, righe:[riga_id]} → evidenzia le righe invece di
        // lasciare l'operatore a cercare quale delle venti manca.
        if (detail && typeof detail === 'object' && Array.isArray((detail as { righe?: unknown }).righe)) {
          const d = detail as { message?: unknown; righe: string[] };
          setRigheIncomplete(d.righe);
          setError(typeof d.message === 'string' ? d.message : t('fatturazione.bozza.errore_generico'));
        } else if (detail && typeof detail === 'object' && Array.isArray((detail as { uscite?: unknown }).uscite)) {
          // 422 che riguarda l'USCITA INTERA, non una sua riga: regime (cliente
          // diventato privato) oppure uscita senza righe da fatturare. In
          // entrambi i casi i codici stanno gia' nel messaggio e non c'e' una
          // riga da evidenziare — non esiste ancora, o la bozza va rifatta.
          setError(typeof (detail as { message?: unknown }).message === 'string'
            ? (detail as { message: string }).message : t('fatturazione.bozza.errore_generico'));
        } else if (detail && typeof detail === 'object' && Array.isArray((detail as { problemi?: unknown }).problemi)) {
          const d = detail as { message?: unknown; problemi: IdoneitaProblema[] };
          setEmitProblemi(d.problemi);
          setEmitTitolo(typeof d.message === 'string' ? d.message : t('fatturazione.bozza.dati_mancanti'));
        } else {
          setError(apiDetail(e, t('fatturazione.bozza.errore_generico')));
        }
      } finally { setBusy(false); }
    })();
  }
  function chiediEmetti() {
    if (pendingCount > 0) { setPendingGuard('emetti'); return; }
    setConfirm('emetti');
  }
  // "Salva e continua": salva il batch poi procede; se un save fallisce → stop + errore.
  function confermaGuard() {
    const azione = pendingGuard;
    void (async () => {
      setBusy(true); setError(null);
      try {
        await salvaPendenti();
        setPendingGuard(null);
        if (azione === 'genera') doGeneraProforma();
        else if (azione === 'emetti') setConfirm('emetti');
      } catch (e) {
        setPendingGuard(null);
        setError(apiDetail(e, t('fatturazione.bozza.errore_generico')));   // NON procedere
      } finally { setBusy(false); }
    })();
  }
  function scarta() {
    void run(() => scartaBozza(bozzaId), () => onScartata());
  }

  if (loading) return <p className="text-sm text-ink-soft p-2">{t('fatturazione.bozza.caricamento')}</p>;
  if (!bozza) return (
    <div className="p-2">
      <p className="text-sm text-accent-danger mb-3">{error ?? t('fatturazione.bozza.errore_carica')}</p>
      <button type="button" onClick={onClose} className="text-sm text-sage-dark hover:underline">{t('fatturazione.bozza.indietro')}</button>
    </div>
  );

  return (
    <div data-testid="bozza-workspace">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark" data-testid="btn-indietro" aria-label={t('fatturazione.bozza.indietro')}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 className="text-lg font-semibold text-ink-dark">{t('fatturazione.bozza.titolo')}</h2>
          <p className="text-sm text-ink-soft" data-testid="bozza-cliente">{bozza.customer_ragione_sociale}</p>
        </div>
      </div>

      {error && <p className="text-sm text-accent-danger mb-3" data-testid="bozza-error">{error}</p>}

      {/* C7: banner proattivo campi mancanti FatturaPA (si aggiorna a ogni applyBozza). */}
      {!bozza.idoneita.ok && (
        <div className="mb-3">
          <IdoneitaProblemiList problemi={bozza.idoneita.problemi} customerId={bozza.customer_id}
            titolo={t('fatturazione.bozza.dati_mancanti')} testid="idoneita-banner" />
        </div>
      )}
      {/* C7: stessi campi, ma restituiti dal 422 dell'emissione. */}
      {emitProblemi && (
        <div className="mb-3">
          <IdoneitaProblemiList problemi={emitProblemi} customerId={bozza.customer_id}
            titolo={emitTitolo} testid="idoneita-emit-banner" />
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_300px] gap-5">
        <div className="space-y-5">
          {/* USCITE (bozza di VENDITA). Sostituisce i conferimenti, non li
              affianca: una bozza ha un canale solo, e il BE non permette di
              agganciare gli uni alle altre. */}
          {vendita && (
          <section className="bg-white border border-stone-mid rounded-xl overflow-hidden" data-testid="sez-bozza-uscite">
            <div className="px-4 py-2.5 bg-stone-bg">
              <h3 className="text-sm font-semibold text-ink-dark">{t('fatturazione.bozza.uscite')}</h3>
            </div>
            {/* DUE LIVELLI (V76): l'USCITA è il gruppo — un formulario, un
                regime — e sotto ci stanno le sue RIGHE, una per tipologia di
                big bag. Il prezzo si digita sulla riga perché è la tipologia a
                deciderlo: un'uscita di alluminio può portare FV e infissi
                sullo stesso FIR, a due prezzi diversi. */}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-ink-soft uppercase">
                  <th className="px-3 py-2">{t('fatturazione.bozza.col_riga')}</th>
                  <th className="px-3 py-2 text-right">{t('fatturazione.bozza.col_peso_fatt')}</th>
                  <th className="px-3 py-2 text-right">{t('fatturazione.bozza.col_prezzo_kg')}</th>
                  <th className="px-3 py-2 text-right">{t('fatturazione.bozza.col_importo')}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {bozza.uscite.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-4 text-ink-soft text-center" data-testid="bozza-no-uscite">{t('fatturazione.bozza.no_uscite')}</td></tr>
                ) : bozza.uscite.map((u) => {
                  const regDir = regimeDirty(u);
                  return (
                    <Fragment key={u.uscita_id}>
                      {/* TESTATA DELL'USCITA: documento di trasporto e regime. */}
                      <tr className={'border-t-2 border-stone-mid bg-stone-bg/40'
                            + (regDir ? ' bg-accent-warning-bg' : '')}
                        data-testid={`uscita-${u.uscita_id}`}>
                        <td className="px-3 py-2.5 align-top font-medium" colSpan={3}>
                          <button type="button" onClick={() => navigate(`/uscite/${u.uscita_id}`)}
                            data-testid={`uscita-link-${u.uscita_id}`} className="text-sage-dark hover:underline font-mono text-xs">
                            {u.codice}
                          </button>
                          <span className="block text-[11px] text-ink-faint font-normal">{u.descrizione_rifiuto} · {u.cer_code}</span>
                          <span className="block text-[11px] text-ink-faint font-normal">{t('fatturazione.bozza.peso_reale', { kg: fmtKg(u.peso_kg) })}</span>
                          {regDir && <span className="block text-[10px] font-semibold text-accent-warning uppercase" data-testid={`uscita-dirty-${u.uscita_id}`}>{t('fatturazione.bozza.non_salvato')}</span>}
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <span className="text-[11px] text-ink-soft uppercase">{t('fatturazione.bozza.col_regime')}</span>
                            {/* Il REGIME è dell'USCITA, non della riga: fra le
                                righe di una stessa uscita non può divergere. */}
                            <select className={inputCls + ' text-[11px] py-1'} data-testid={`uscita-regime-${u.uscita_id}`}
                              value={regimeEdits[u.uscita_id] ?? u.regime_iva} disabled={busy || clientePrivato}
                              title={clientePrivato ? t('fatturazione.bozza.regime_bloccato') : undefined}
                              onChange={(ev) => patchRegime(u.uscita_id, ev.target.value as RegimeIva)}>
                              {REGIMI_IVA.map((r) => (
                                <option key={r} value={r}>
                                  {t(r === 'reverse_charge'
                                    ? 'settings.cer_rifiuti.regime_reverse_charge'
                                    : 'settings.cer_rifiuti.regime_iva_ordinaria')}
                                </option>
                              ))}
                            </select>
                            {/* DERIVATI del regime, in sola lettura: aliquota,
                                Natura e dicitura nascono insieme lato backend e
                                non si toccano a mano. */}
                            <span className="text-[11px] text-ink-faint" data-testid={`uscita-derivati-${u.uscita_id}`}>
                              {t('fatturazione.bozza.aliquota_label', { v: Number(u.aliquota_iva) })}
                              {u.natura ? ` · ${u.natura}` : ''}
                            </span>
                          </div>
                          {clientePrivato && (
                            <span className="block text-[10px] text-ink-faint mt-0.5" data-testid={`uscita-regime-nota-${u.uscita_id}`}>
                              {t('fatturazione.bozza.regime_bloccato')}
                            </span>
                          )}
                          {u.dicitura && <span className="block text-[10px] text-ink-faint italic mt-0.5">{u.dicitura}</span>}
                        </td>
                        {/* Totale del GRUPPO: viene dal backend, non dai campi a
                            video — le righe in corso di digitazione hanno il
                            loro importo sotto. */}
                        <td className="px-3 py-2.5 text-right align-top font-medium" data-testid={`uscita-importo-${u.uscita_id}`}>{eur(u.imponibile)}</td>
                        <td className="px-3 py-2.5 text-right align-top w-10">
                          <button type="button" onClick={() => removeUscita(u.uscita_id)} disabled={busy}
                            data-testid={`btn-rimuovi-uscita-${u.uscita_id}`} className="text-red-600 hover:text-red-800 disabled:opacity-40" aria-label={t('fatturazione.bozza.rimuovi')}>
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                      {u.righe.map((r) => {
                        const e = rigaEdits[r.riga_id];
                        const dir = rigaDirty(r);
                        const incompleta = righeIncomplete.includes(r.riga_id);
                        // Importo CALCOLATO sui valori a video: mostra l'effetto
                        // della riga che si sta compilando. I TOTALI restano
                        // quelli del backend, che sa di IVA, arrotondamenti e
                        // voci extra.
                        const peso = Number(e?.peso_fatturazione_kg ?? 0);
                        const prezzo = Number(e?.prezzo_unitario ?? 0);
                        const importo = Number.isFinite(peso * prezzo) ? peso * prezzo : 0;
                        const corretto = e !== undefined
                          && Number(e.peso_fatturazione_kg) !== Number(r.peso_big_bag_kg);
                        return (
                          <tr key={r.riga_id}
                            className={'border-t border-stone-mid/60'
                              + (incompleta ? ' bg-accent-danger-bg' : dir ? ' bg-accent-warning-bg' : '')}
                            data-testid={`riga-${r.riga_id}`}>
                            <td className="px-3 py-2.5 align-top pl-8">
                              <span className="font-medium" data-testid={`riga-tipologia-${r.riga_id}`}>
                                {r.tipologia_nome ?? t('fatturazione.bozza.tipologia_non_specificata')}
                              </span>
                              {/* DA DOVE VIENE IL NUMERO: i sacchi che compongono
                                  la riga, così chi corregge il peso sa cosa sta
                                  cambiando. `<details>` nativo: nessuno stato da
                                  tenere, nessun componente da scrivere. */}
                              <details className="mt-0.5" data-testid={`riga-sacchi-${r.riga_id}`}>
                                <summary className="text-[11px] text-ink-faint cursor-pointer">
                                  {t('fatturazione.bozza.sacchi_riepilogo', {
                                    n: r.big_bags.length, kg: fmtKg(r.peso_big_bag_kg) })}
                                </summary>
                                <ul className="mt-1 space-y-0.5">
                                  {r.big_bags.map((b) => (
                                    <li key={b.id} className="text-[11px] text-ink-faint font-mono">
                                      {b.qr_token} · {fmtKg(b.peso_kg)} kg
                                      {b.data_produzione ? ` · ${new Date(b.data_produzione).toLocaleDateString('it-IT')}` : ''}
                                    </li>
                                  ))}
                                </ul>
                              </details>
                              {dir && <span className="block text-[10px] font-semibold text-accent-warning uppercase" data-testid={`riga-dirty-${r.riga_id}`}>{t('fatturazione.bozza.non_salvato')}</span>}
                              {incompleta && <span className="block text-[10px] font-semibold text-accent-danger uppercase" data-testid={`riga-incompleta-${r.riga_id}`}>{t('fatturazione.bozza.riga_incompleta')}</span>}
                            </td>
                            <td className="px-3 py-2.5 text-right align-top">
                              <input type="number" step="0.01" min="0" className={numCls + ' w-24'}
                                data-testid={`riga-peso-${r.riga_id}`} value={e?.peso_fatturazione_kg ?? ''}
                                onChange={(ev) => patchRiga(r.riga_id, { peso_fatturazione_kg: ev.target.value })} />
                              {/* Il peso di partenza compare solo quando è stato
                                  corretto: altrimenti ripeterebbe il campo. */}
                              {corretto && (
                                <span className="block text-[10px] text-ink-faint mt-0.5" data-testid={`riga-origine-${r.riga_id}`}>
                                  {t('fatturazione.bozza.peso_origine', { kg: fmtKg(r.peso_big_bag_kg) })}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right align-top">
                              {/* 4 decimali come la colonna `prezzo_unitario NUMERIC(12,4)`:
                                  uno step a 2 impedirebbe di digitare un €/kg reale.
                                  MA il documento fiscale ne stampa 2 (`_fmt` del
                                  builder XML), quindi un prezzo coi millesimi
                                  finisce ARROTONDATO in fattura: scelta presa, i
                                  prezzi di vendita stanno sull'ordine di 2,90 €/kg. */}
                              <input type="number" step="0.0001" min="0" className={numCls + ' w-24'}
                                data-testid={`riga-prezzo-${r.riga_id}`} value={e?.prezzo_unitario ?? ''}
                                placeholder={t('fatturazione.bozza.prezzo_placeholder')}
                                onChange={(ev) => patchRiga(r.riga_id, { prezzo_unitario: ev.target.value })} />
                            </td>
                            <td className="px-3 py-2.5 text-right align-top" data-testid={`riga-importo-${r.riga_id}`}>{eur(importo)}</td>
                            <td className="px-3 py-2.5 w-10"></td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </section>
          )}

          {/* CONFERIMENTI */}
          {!vendita && (
          <section className="bg-white border border-stone-mid rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-stone-bg">
              <h3 className="text-sm font-semibold text-ink-dark">{t('fatturazione.bozza.conferimenti')}</h3>
              <button type="button" onClick={openAddConf} disabled={busy} data-testid="btn-aggiungi-conferimenti"
                className="inline-flex items-center gap-1 text-xs text-sage-dark hover:underline disabled:opacity-40">
                <Plus size={14} /> {t('fatturazione.bozza.aggiungi_conferimenti')}
              </button>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {bozza.conferimenti.length === 0 ? (
                  <tr><td className="px-4 py-4 text-ink-soft text-center" data-testid="bozza-no-conferimenti">{t('fatturazione.bozza.no_conferimenti')}</td></tr>
                ) : bozza.conferimenti.map((c) => {
                  const ivaDir = ivaDirty(c);
                  const confDirty = ivaDir || trasportoDirty(c) || !!dicituraPending[`mat-${c.conferimento_id}`] || !!dicituraPending[`tra-${c.conferimento_id}`];
                  return (
                  <tr key={c.conferimento_id} className={'border-t border-stone-mid/60' + (confDirty ? ' bg-accent-warning-bg' : '')} data-testid={`conf-${c.conferimento_id}`}>
                    <td className="px-4 py-2.5 font-medium">
                      <button type="button" onClick={() => navigate(`/conferimenti/${c.conferimento_id}`)}
                        data-testid={`conf-link-${c.conferimento_id}`} className="text-sage-dark hover:underline">
                        {c.numero ?? c.codice_ordine}
                      </button>
                      <span className="block text-[11px] text-ink-faint font-normal">{categoriaLabel(c.categoria, categorie)}</span>
                      {confDirty && <span className="block text-[10px] font-semibold text-accent-warning uppercase" data-testid={`conf-dirty-${c.conferimento_id}`}>{t('fatturazione.bozza.non_salvato')}</span>}
                    </td>
                    {/* Il trasporto e' EDITABILE e AZZERABILE, e l'input resta
                        visibile anche a 0: se sparisse una volta azzerato, non
                        ci sarebbe piu' modo di rimetterlo. «Torna alla tariffa»
                        compare solo quando un override c'e' davvero. */}
                    <td className="px-4 py-2.5 text-right">
                      {eur(c.imponibile_materiale)}
                      <span className="mt-1 flex items-center justify-end gap-1">
                        <label className="text-[11px] text-ink-faint" htmlFor={`conf-trasporto-${c.conferimento_id}`}>
                          {t('fatturazione.bozza.col_trasporto')}
                        </label>
                        <input type="number" step="0.01" min="0" className={numCls + ' w-20'}
                          id={`conf-trasporto-${c.conferimento_id}`}
                          data-testid={`conf-trasporto-${c.conferimento_id}`}
                          value={trasportoEdits[c.conferimento_id] ?? ''}
                          onChange={(e) => patchTrasporto(c.conferimento_id, e.target.value)} />
                      </span>
                      {c.importo_trasporto_override !== null && (
                        <button type="button" disabled={busy}
                          data-testid={`conf-trasporto-reset-${c.conferimento_id}`}
                          onClick={() => resetTrasporto(c.conferimento_id)}
                          className="block ml-auto mt-0.5 text-[10px] text-sage-dark hover:text-sage underline disabled:opacity-40">
                          {t('fatturazione.bozza.trasporto_reset')}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <label className="text-[11px] text-ink-faint">{t('fatturazione.bozza.col_iva_materiale')}</label>
                        <input type="number" step="0.01" min="0" max="100" className={numCls + ' w-14'}
                          data-testid={`conf-iva-mat-${c.conferimento_id}`}
                          value={ivaEdits[c.conferimento_id]?.iva_riga ?? ''}
                          onChange={(e) => patchIva(c.conferimento_id, { iva_riga: e.target.value })} />
                        <label className="text-[11px] text-ink-faint">{t('fatturazione.bozza.col_iva_trasporto')}</label>
                        <input type="number" step="0.01" min="0" max="100" className={numCls + ' w-14'}
                          data-testid={`conf-iva-tra-${c.conferimento_id}`}
                          value={ivaEdits[c.conferimento_id]?.iva_trasporto ?? ''}
                          onChange={(e) => patchIva(c.conferimento_id, { iva_trasporto: e.target.value })} />
                      </div>
                      {/* Override dicitura per tipo (materiale sempre; trasporto se presente). */}
                      <div className="mt-1.5 flex flex-col items-end gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-ink-faint uppercase">{t('fatturazione.bozza.dicitura.materiale')}</span>
                          <DicituraControl key={`${c.dicitura_materiale_modo}|${c.dicitura_materiale_manuale}`}
                            idBase={`mat-${c.conferimento_id}`} disabled={busy}
                            modo={c.dicitura_materiale_modo} manuale={c.dicitura_materiale_manuale}
                            onDirtyChange={(dirty, modo, testo) => setDicPending(`mat-${c.conferimento_id}`, dirty,
                              () => aggiornaDicituraConferimentoBozza(bozzaId, c.conferimento_id, { tipo: 'materiale', dicitura_modo: modo, dicitura_manuale: modo === 'manuale' ? testo : null }))} />
                        </div>
                        {Number(c.imponibile_trasporto) > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-ink-faint uppercase">{t('fatturazione.bozza.dicitura.trasporto')}</span>
                            <DicituraControl key={`${c.dicitura_trasporto_modo}|${c.dicitura_trasporto_manuale}`}
                              idBase={`tra-${c.conferimento_id}`} disabled={busy}
                              modo={c.dicitura_trasporto_modo} manuale={c.dicitura_trasporto_manuale}
                              onDirtyChange={(dirty, modo, testo) => setDicPending(`tra-${c.conferimento_id}`, dirty,
                                () => aggiornaDicituraConferimentoBozza(bozzaId, c.conferimento_id, { tipo: 'trasporto', dicitura_modo: modo, dicitura_manuale: modo === 'manuale' ? testo : null }))} />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right w-10">
                      <button type="button" onClick={() => removeConf(c.conferimento_id)} disabled={busy}
                        data-testid={`btn-rimuovi-conf-${c.conferimento_id}`} className="text-red-600 hover:text-red-800 disabled:opacity-40" aria-label={t('fatturazione.bozza.rimuovi')}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {showAddConf && (
              <div className="border-t border-stone-mid p-3 bg-stone-bg/40" data-testid="add-conf-panel">
                {fatturabili.length === 0 ? (
                  <p className="text-xs text-ink-soft">{t('fatturazione.bozza.no_fatturabili')}</p>
                ) : fatturabili.map((it) => (
                  <label key={it.conferimento_id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                    <input type="checkbox" checked={selConf.has(it.conferimento_id)} data-testid={`add-chk-${it.conferimento_id}`}
                      onChange={() => setSelConf((p) => { const n = new Set(p); if (n.has(it.conferimento_id)) n.delete(it.conferimento_id); else n.add(it.conferimento_id); return n; })} />
                    {it.numero ?? it.codice_ordine} · {categoriaLabel(it.categoria, categorie)}
                  </label>
                ))}
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={confirmAddConf} disabled={busy || selConf.size === 0} data-testid="btn-conferma-aggiungi-conf"
                    className="px-3 py-1.5 text-xs font-medium rounded bg-sage text-white disabled:opacity-40">{t('fatturazione.bozza.aggiungi')}</button>
                  <button type="button" onClick={() => setShowAddConf(false)} className="px-3 py-1.5 text-xs text-ink-soft hover:underline">{t('common.azioni.annulla')}</button>
                </div>
              </div>
            )}
          </section>
          )}

          {/* VOCI EXTRA */}
          <section className="bg-white border border-stone-mid rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 bg-stone-bg"><h3 className="text-sm font-semibold text-ink-dark">{t('fatturazione.bozza.voci_extra')}</h3></div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-ink-soft uppercase">
                  <th className="px-3 py-2">{t('fatturazione.bozza.col_descrizione')}</th>
                  <th className="px-3 py-2 text-right">{t('fatturazione.bozza.col_quantita')}</th>
                  <th className="px-3 py-2 text-right">{t('fatturazione.bozza.col_prezzo')}</th>
                  <th className="px-3 py-2 text-right">{t('fatturazione.bozza.col_iva')}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {bozza.voci_extra.map((v) => {
                  const e = edits[v.id] ?? EMPTY_DRAFT;
                  const upd = (patch: Partial<ExtraDraft>) => setEdits((m) => ({ ...m, [v.id]: { ...m[v.id], ...patch } }));
                  const voceDir = voceDirty(v);
                  const rowDirty = voceDir || !!dicituraPending[`extra-${v.id}`];
                  return (
                    <tr key={v.id} className={'border-t border-stone-mid/60' + (rowDirty ? ' bg-accent-warning-bg' : '')} data-testid={`voce-extra-${v.id}`}>
                      <td className="px-3 py-2 align-top">
                        <input className={inputCls + ' w-full'} value={e.descrizione} data-testid={`extra-desc-${v.id}`} onChange={(ev) => upd({ descrizione: ev.target.value })} />
                        {rowDirty && <span className="block text-[10px] font-semibold text-accent-warning uppercase mt-0.5" data-testid={`extra-dirty-${v.id}`}>{t('fatturazione.bozza.non_salvato')}</span>}
                      </td>
                      <td className="px-3 py-2 text-right align-top"><input className={numCls + ' w-16'} type="number" value={e.quantita} onChange={(ev) => upd({ quantita: ev.target.value })} /></td>
                      <td className="px-3 py-2 text-right align-top"><input className={numCls + ' w-20'} type="number" value={e.prezzo_unitario_eur} data-testid={`extra-prezzo-${v.id}`} onChange={(ev) => upd({ prezzo_unitario_eur: ev.target.value })} /></td>
                      <td className="px-3 py-2 text-right align-top">
                        {/* In inversione contabile le voci extra SEGUONO la
                            cessione: il backend le riscrive a 0 + N6.1
                            qualunque aliquota arrivi. Il campo editabile
                            mostrerebbe un valore che il salvataggio smentisce. */}
                        {reverseCharge ? (
                          <span className="text-[11px] text-ink-faint" data-testid={`extra-aliquota-bloccata-${v.id}`}>
                            {t('fatturazione.bozza.aliquota_reverse')}
                          </span>
                        ) : (
                          <input className={numCls + ' w-14'} type="number" value={e.aliquota_iva} onChange={(ev) => upd({ aliquota_iva: ev.target.value })} />
                        )}
                        {/* Dicitura sotto la cella IVA, come nelle righe conferimento. */}
                        <div className="mt-1.5 flex justify-end">
                          <DicituraControl key={`${v.dicitura_modo}|${v.dicitura_manuale}`}
                            idBase={`extra-${v.id}`} disabled={busy}
                            modo={v.dicitura_modo} manuale={v.dicitura_manuale}
                            onDirtyChange={(dirty, modo, testo) => setDicPending(`extra-${v.id}`, dirty,
                              () => modificaVoceExtra(bozzaId, v.id, { dicitura_modo: modo, dicitura_manuale: modo === 'manuale' ? testo : null }))} />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap align-top">
                        <button type="button" onClick={() => delVoce(v.id)} disabled={busy} data-testid={`btn-elimina-extra-${v.id}`} className="text-red-600 hover:text-red-800 disabled:opacity-40" aria-label={t('fatturazione.bozza.rimuovi')}><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  );
                })}
                {/* riga di aggiunta */}
                <tr className="border-t border-stone-mid bg-stone-bg/30">
                  <td className="px-3 py-2 align-top"><input className={inputCls + ' w-full'} placeholder={t('fatturazione.bozza.placeholder_descrizione')} value={draft.descrizione} data-testid="extra-new-desc" onChange={(e) => setDraft({ ...draft, descrizione: e.target.value })} /></td>
                  <td className="px-3 py-2 text-right align-top"><input className={numCls + ' w-16'} type="number" value={draft.quantita} data-testid="extra-new-quantita" onChange={(e) => setDraft({ ...draft, quantita: e.target.value })} /></td>
                  <td className="px-3 py-2 text-right align-top"><input className={numCls + ' w-20'} type="number" value={draft.prezzo_unitario_eur} data-testid="extra-new-prezzo" onChange={(e) => setDraft({ ...draft, prezzo_unitario_eur: e.target.value })} /></td>
                  <td className="px-3 py-2 text-right align-top">
                    {reverseCharge ? (
                      <span className="text-[11px] text-ink-faint" data-testid="extra-new-aliquota-bloccata">{t('fatturazione.bozza.aliquota_reverse')}</span>
                    ) : (
                      <input className={numCls + ' w-14'} type="number" value={draft.aliquota_iva} data-testid="extra-new-aliquota" onChange={(e) => setDraft({ ...draft, aliquota_iva: e.target.value })} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-right align-top"><button type="button" onClick={addVoce} disabled={busy} data-testid="btn-aggiungi-extra" className="text-sage-dark hover:text-sage disabled:opacity-40" aria-label={t('fatturazione.bozza.aggiungi')}><Plus size={16} /></button></td>
                </tr>
              </tbody>
            </table>
            <p className="text-[11px] text-ink-faint px-3 py-2">{t('fatturazione.bozza.nota_extra')}</p>
          </section>
        </div>

        {/* RIEPILOGO + AZIONI */}
        <div className="bg-white border border-stone-mid rounded-xl p-4 h-fit">
          <div className="text-sm space-y-1 border-b border-stone-mid pb-3 mb-3">
            <div className="flex justify-between"><span>{t('fatturazione.bozza.imponibile')}</span><span data-testid="bozza-imponibile">{eur(bozza.importo_netto)}</span></div>
            <div className="flex justify-between text-ink-soft"><span>{t('fatturazione.bozza.iva')}</span><span data-testid="bozza-iva">{eur(bozza.importo_iva)}</span></div>
            <div className="flex justify-between font-semibold text-base pt-1"><span>{t('fatturazione.bozza.totale')}</span><span data-testid="bozza-totale">{eur(bozza.importo_totale)}</span></div>
          </div>

          {/* Scadenza di pagamento (V72). Su TUTTE le bozze, non solo vendita:
              prima della V72 l'XML emetteva DataScadenzaPagamento == data
              documento su ogni fattura. Campo vuoto = default d'impianto
              all'emissione, ed e' un valore, non un "non so". */}
          <div className="border-b border-stone-mid pb-3 mb-3">
            <label className="block text-xs text-ink-soft mb-1" htmlFor="bozza-scadenza">{t('fatturazione.bozza.scadenza')}</label>
            <input id="bozza-scadenza" type="date" className={inputCls + ' w-full'} data-testid="bozza-scadenza"
              value={scadenzaValore} onChange={(ev) => setScadenza(ev.target.value)} />
            <p className="text-[11px] text-ink-faint mt-1">{t('fatturazione.bozza.scadenza_hint', { n: giorniScadenza })}</p>
          </div>

          {/* Metodo di pagamento (#4): override per-fattura; "" = predefinito impianto. */}
          <div className="border-b border-stone-mid pb-3 mb-3">
            <label className="block text-xs text-ink-soft mb-1">{t('fatturazione.bozza.metodo_pagamento')}</label>
            <select className={inputCls + ' w-full cursor-pointer'} data-testid="metodo-pagamento"
              value={bozza.metodo_pagamento_override ?? ''}
              onChange={(e) => saveMetodoPagamento(e.target.value)}>
              <option value="">{t('fatturazione.bozza.metodo_predefinito', { label: t(`fatturazione.bozza.modalita.${bozza.metodo_pagamento_default}`, { defaultValue: bozza.metodo_pagamento_default }) })}</option>
              {METODI_PAGAMENTO.map((m) => <option key={m} value={m}>{t(`fatturazione.bozza.modalita.${m}`)}</option>)}
            </select>
          </div>

          <div className="space-y-2">
            <button type="button" onClick={salvaTutto} disabled={busy || pendingCount === 0} data-testid="btn-salva-tutto"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold rounded bg-sage text-white hover:bg-sage-dark disabled:opacity-40 disabled:cursor-not-allowed">
              <Check size={15} /> {pendingCount > 0 ? t('fatturazione.bozza.salva_tutto_n', { n: pendingCount }) : t('fatturazione.bozza.salva_tutto')}
            </button>
            <button type="button" onClick={generaProforma} data-testid="btn-genera-proforma"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded border border-sage text-sage-dark hover:bg-sage-light">
              <FileText size={15} /> {t('fatturazione.bozza.genera_proforma')}
            </button>
            <button type="button" onClick={() => setShowInvia(true)} data-testid="btn-invia-proforma"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded border border-sage text-sage-dark hover:bg-sage-light">
              <Mail size={15} /> {t('fatturazione.bozza.invia_proforma')}
            </button>

            {confirm === 'emetti' ? (
              <div className="rounded border border-stone-mid p-2 text-xs" data-testid="confirm-emetti">
                <p className="mb-2 text-ink-mid">{t('fatturazione.bozza.conferma_emetti')}</p>
                <div className="flex gap-2">
                  <button type="button" onClick={emetti} disabled={busy} data-testid="btn-conferma-emetti" className="px-3 py-1.5 rounded bg-sage text-white font-medium disabled:opacity-40">{t('fatturazione.bozza.si_emetti')}</button>
                  <button type="button" onClick={() => setConfirm(null)} className="px-3 py-1.5 text-ink-soft hover:underline">{t('common.azioni.annulla')}</button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={chiediEmetti} disabled={busy} data-testid="btn-emetti"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded bg-sage text-white disabled:opacity-40">
                <Send size={15} /> {t('fatturazione.bozza.emetti')}
              </button>
            )}

            {confirm === 'scarta' ? (
              <div className="rounded border border-stone-mid p-2 text-xs" data-testid="confirm-scarta">
                <p className="mb-2 text-ink-mid">{t('fatturazione.bozza.conferma_scarta')}</p>
                <div className="flex gap-2">
                  <button type="button" onClick={scarta} disabled={busy} data-testid="btn-conferma-scarta" className="px-3 py-1.5 rounded bg-red-600 text-white font-medium disabled:opacity-40">{t('fatturazione.bozza.si_scarta')}</button>
                  <button type="button" onClick={() => setConfirm(null)} className="px-3 py-1.5 text-ink-soft hover:underline">{t('common.azioni.annulla')}</button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirm('scarta')} disabled={busy} data-testid="btn-scarta"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded border border-red-300 text-red-700 hover:bg-red-50">
                <X size={15} /> {t('fatturazione.bozza.scarta')}
              </button>
            )}
          </div>
        </div>
      </div>

      {showInvia && (
        <ProformaInviaModal bozzaId={bozzaId} ragioneSociale={bozza.customer_ragione_sociale}
          emailDefault={bozza.customer_email ?? ''}
          onClose={() => setShowInvia(false)} onSent={() => setShowInvia(false)} />
      )}

      {/* Guard "modifiche non salvate" prima di Genera pro forma / Emetti. */}
      {pendingGuard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="guard-pendenti">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPendingGuard(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5">
            <h3 className="text-base font-semibold text-ink-dark mb-2">{t('fatturazione.bozza.guard_titolo')}</h3>
            <p className="text-sm text-ink-mid mb-4">{t('fatturazione.bozza.guard_testo', { n: pendingCount })}</p>
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setPendingGuard(null)} disabled={busy}
                className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg disabled:opacity-50">{t('common.azioni.annulla')}</button>
              <button type="button" onClick={confermaGuard} disabled={busy} data-testid="btn-salva-e-continua"
                className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark disabled:opacity-60">{t('fatturazione.bozza.guard_salva_continua')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
